import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

// Upload profile picture and return download URL
// Path matches storage.rules: profile-pictures/{userId}/{fileName}
export const uploadProfilePicture = async (userId, file) => {
  const ext = file.name.split('.').pop();
  const fileName = `profile.${ext}`;
  const storageRef = ref(storage, `profile-pictures/${userId}/${fileName}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// Upload post media (images and/or video) and return download URLs
// Paths match storage.rules: post-images/{userId}/{postId}/{fileName} and post-videos/{userId}/{postId}/{fileName}
// onProgress callback receives a value from 0 to 100
export const uploadPostMedia = async (postId, files, userId, onProgress) => {
  const urls = [];
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  let uploadedSize = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const ext = file.name.split('.').pop();
    const isVideo = file.type.startsWith('video/');
    const folder = isVideo ? 'post-videos' : 'post-images';
    const fileName = `${i}.${ext}`;
    const storageRef = ref(storage, `${folder}/${userId}/${postId}/${fileName}`);

    const url = await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed',
        (snapshot) => {
          if (onProgress) {
            const fileProgress = snapshot.bytesTransferred;
            const overall = ((uploadedSize + fileProgress) / totalSize) * 100;
            onProgress(Math.round(overall));
          }
        },
        (error) => reject(error),
        async () => {
          uploadedSize += file.size;
          const downloadUrl = await getDownloadURL(task.snapshot.ref);
          resolve(downloadUrl);
        }
      );
    });

    urls.push({ url, type: isVideo ? 'video' : 'image', name: file.name });
  }

  if (onProgress) onProgress(100);
  return urls;
};

// Upload a single media file for a chat message and return { url, type }
export const uploadMessageMedia = async (file, userId, conversationId) => {
  const ext = file.name.split('.').pop();
  const isVideo = file.type.startsWith('video/');
  const folder = isVideo ? 'message-videos' : 'message-images';
  const fileName = `${Date.now()}.${ext}`;
  const storageRef = ref(storage, `${folder}/${userId}/${conversationId}/${fileName}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, type: isVideo ? 'video' : 'image' };
};

// Upload a single file and return its download URL
export const uploadFile = async (path, file) => {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// Upload a swing photo or video and return { url, type }
export const uploadSwingMedia = async (file, userId) => {
  const ext = file.name.split('.').pop();
  const isVideo = file.type.startsWith('video/');
  const folder = isVideo ? 'swing-videos' : 'swing-images';
  const fileName = `${Date.now()}.${ext}`;
  const storageRef = ref(storage, `${folder}/${userId}/${fileName}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, type: isVideo ? 'video' : 'image' };
};

// Delete a file from storage
export const deleteFile = async (path) => {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
};
